import {
  SELLERBOX_PLUS_MONTHLY_AMOUNT,
  getSellerboxPlusPaymentPolicy,
} from '../../constants/subscription.ts';
import { ROUTES } from '../../routes/paths.ts';

export type MobileMembershipPromotion = {
  enabled: boolean;
  badgeText: string;
  heroTitle: string;
  summaryText: string;
  priceDescription: string;
  periodText: string;
  discountMonths: number;
  discountAmount: number;
};

export type MobileMembershipPaymentSummary = {
  currentPaymentLabel: string;
  regularAmountLabel: string;
  billingCycleLabel: string;
  noticeText: string;
  ctaLabel: string;
};

export type MobileMembershipCurrentSubscription = {
  subscriptionNo?: number;
  channel: string;
  expireDt: string;
  isExpiringSoon: boolean;
};

export type MobileMembershipPendingSubscription = {
  exists: boolean;
  eligibleDt: string;
  maxCount: number;
  paymentResultId: number;
  status: string;
};

export type MobileMembershipGuidance = {
  type: string | null;
  headline: string;
  ctaLabel: string;
};

export const PC_HERO_BADGE_EARLY_BIRD_TYPE = 'EARLY_BIRD';
export const PC_HERO_BADGE_STANDARD_TYPE = 'STANDARD';
export const PC_HERO_BADGE_FIRST_PAYMENT_TYPE = 'FIRST_PAYMENT';
const PC_HERO_BADGE_TYPES = new Set([
  PC_HERO_BADGE_EARLY_BIRD_TYPE,
  PC_HERO_BADGE_STANDARD_TYPE,
  PC_HERO_BADGE_FIRST_PAYMENT_TYPE,
]);

export type PcHeroBadgeType =
  | typeof PC_HERO_BADGE_EARLY_BIRD_TYPE
  | typeof PC_HERO_BADGE_STANDARD_TYPE
  | typeof PC_HERO_BADGE_FIRST_PAYMENT_TYPE;

export type PcHeroBadge = {
  enabled: boolean;
  label: string;
  type: PcHeroBadgeType;
};

export type MobileMembershipPolicy = {
  productName: string;
  subscriptionNo: number;
  billingCycle: string;
  membershipState?: string;
  hasBillingMethod: boolean;
  pendingPaymentResultId?: string;
  currentSubscription: MobileMembershipCurrentSubscription | null;
  pendingSubscription: MobileMembershipPendingSubscription;
  guidance: MobileMembershipGuidance;
  isAnySubscribed: boolean;
  isPrePayment: boolean;
  regularAmount: number;
  paymentAmount: number;
  nextPaymentAmount: number;
  currency: string;
  trialDays: number;
  promotion: MobileMembershipPromotion;
  paymentSummary: MobileMembershipPaymentSummary;
  pcHeroBadge: PcHeroBadge;
};

type MobilePaymentPolicy = Pick<
  MobileMembershipPolicy,
  'subscriptionNo' | 'isPrePayment' | 'isAnySubscribed'
>;

export const MOBILE_MEMBERSHIP_CHANNEL = 'WEB_MOBILE';
export const MOBILE_MEMBERSHIP_GUEST_STATE = 'GUEST';
export const MOBILE_MEMBERSHIP_WEB_ELIGIBLE_STATE = 'WEB_ELIGIBLE';
export const MOBILE_MEMBERSHIP_WEB_SUBSCRIBED_STATE = 'WEB_SUBSCRIBED';
export const MOBILE_MEMBERSHIP_APP_SUBSCRIBED_STATE = 'APP_SUBSCRIBED';
export const MOBILE_MEMBERSHIP_WEB_CONVERSION_PENDING_STATE =
  'APP_SUBSCRIBED_WEB_CONVERSION_PENDING';
export const MOBILE_MEMBERSHIP_EXPIRED_STATE = 'EXPIRED';
const MOBILE_MEMBERSHIP_STATES = new Set([
  MOBILE_MEMBERSHIP_GUEST_STATE,
  MOBILE_MEMBERSHIP_WEB_ELIGIBLE_STATE,
  MOBILE_MEMBERSHIP_WEB_SUBSCRIBED_STATE,
  MOBILE_MEMBERSHIP_APP_SUBSCRIBED_STATE,
  MOBILE_MEMBERSHIP_WEB_CONVERSION_PENDING_STATE,
  MOBILE_MEMBERSHIP_EXPIRED_STATE,
]);
export const MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_AVAILABLE = 'INAPP_TO_WEB_AVAILABLE';
export const MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_PENDING = 'INAPP_TO_WEB_PENDING';
export const MOBILE_MEMBERSHIP_JUNE_2026_APP_CONVERSION_FLOATING_TITLE =
  '🎉 얼리버드 이벤트\n6월부터 3개월간 월 100원';
export const MOBILE_MEMBERSHIP_JUNE_2026_APP_CONVERSION_CTA_LABEL = '구독하기';
const KOREA_TIME_ZONE = 'Asia/Seoul';

export type MobileCardPaymentPayloadInput = {
  cplatToken: string;
  isAnySubscribed: boolean;
  cardNo: string;
  expirationDate: string;
  password: string;
  cardOwnerInfo: string;
  policy?: MobilePaymentPolicy;
};

type MobilePendingSubscriptionPayloadInput = {
  cplatToken: string;
};

export type MobileCardBillingPayloadInput = {
  cplatToken: string;
  cardNo: string;
  expirationDate: string;
  password: string;
  cardOwnerInfo: string;
};

export type MobileKakaoBillingPayloadInput = {
  cplatToken: string;
  pgToken: string;
  kakaoPayRequestInfo: unknown;
  kakaoPayResponseInfo: unknown;
};

export const buildMobileLoginRedirectPath = (redirectPath: string) => {
  return `${ROUTES.mobileSignin}?redirect=${encodeURIComponent(redirectPath)}`;
};

export const formatKoreanWon = (amount: number) => `${amount.toLocaleString('ko-KR')}원`;

export const getMobileMembershipPaymentDisplay = (isAnySubscribed: boolean) => {
  const policy = getSellerboxPlusPaymentPolicy(isAnySubscribed);

  return {
    ...policy,
    paymentLabel: formatKoreanWon(policy.paymentAmount),
    ctaLabel: `${formatKoreanWon(policy.paymentAmount)} 결제하기`,
  };
};

export const createFallbackMobileMembershipPolicy = (
  isAnySubscribed: boolean,
): MobileMembershipPolicy => {
  const { subscriptionNo, isPrePayment, paymentAmount } =
    getSellerboxPlusPaymentPolicy(isAnySubscribed);
  const regularAmountLabel = formatKoreanWon(SELLERBOX_PLUS_MONTHLY_AMOUNT);
  const currentPaymentLabel = formatKoreanWon(paymentAmount);

  return {
    productName: '셀러박스 멤버십',
    subscriptionNo,
    billingCycle: 'MONTHLY',
    membershipState: undefined,
    hasBillingMethod: false,
    pendingPaymentResultId: undefined,
    currentSubscription: null,
    pendingSubscription: {
      exists: false,
      eligibleDt: '',
      maxCount: 0,
      paymentResultId: 0,
      status: '',
    },
    guidance: {
      type: null,
      headline: '',
      ctaLabel: '',
    },
    isAnySubscribed,
    isPrePayment,
    regularAmount: SELLERBOX_PLUS_MONTHLY_AMOUNT,
    paymentAmount,
    nextPaymentAmount: SELLERBOX_PLUS_MONTHLY_AMOUNT,
    currency: 'KRW',
    trialDays: 0,
    promotion: {
      enabled: false,
      badgeText: '',
      heroTitle: '',
      summaryText: '',
      priceDescription: '',
      periodText: '',
      discountMonths: 0,
      discountAmount: 0,
    },
    paymentSummary: {
      currentPaymentLabel,
      regularAmountLabel,
      billingCycleLabel: '매월 자동 결제',
      noticeText: '멤버십 정책 확인 후 결제가 진행됩니다.',
      ctaLabel: `${currentPaymentLabel} 결제하기`,
    },
    pcHeroBadge: {
      enabled: false,
      label: '',
      type: PC_HERO_BADGE_STANDARD_TYPE,
    },
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const readString = (value: unknown, fallback: string) => {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
};

const readNumber = (value: unknown, fallback: number) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

const readOptionalNumber = (value: unknown) => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const readBoolean = (value: unknown, fallback: boolean) => {
  return typeof value === 'boolean' ? value : fallback;
};

const readField = (
  record: Record<string, unknown>,
  camelCaseKey: string,
  pascalCaseKey: string,
) => {
  return record[camelCaseKey] ?? record[pascalCaseKey];
};

const resolveMembershipState = (value: unknown, fallback?: string) => {
  const state = readString(value, fallback ?? '');

  return MOBILE_MEMBERSHIP_STATES.has(state) ? state : MOBILE_MEMBERSHIP_EXPIRED_STATE;
};

const resolvePcHeroBadgeType = (value: unknown, fallback: PcHeroBadgeType) => {
  const type = readString(value, fallback);

  return PC_HERO_BADGE_TYPES.has(type) ? (type as PcHeroBadgeType) : fallback;
};

const getPositiveInteger = (value: unknown, fallback = 0) => {
  const numericValue = readNumber(value, fallback);

  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : fallback;
};

export const resolveMobileMembershipPolicy = (
  response: unknown,
  fallbackPolicy: MobileMembershipPolicy,
): MobileMembershipPolicy => {
  if (!isRecord(response)) return fallbackPolicy;
  if (response.code !== '200') return fallbackPolicy;

  const responseData = Array.isArray(response.data) ? response.data[0] : response.data;
  if (!isRecord(responseData)) return fallbackPolicy;

  const promotionData = readField(responseData, 'promotion', 'Promotion');
  const paymentSummaryData = readField(responseData, 'paymentSummary', 'PaymentSummary');
  const currentSubscriptionData = readField(
    responseData,
    'currentSubscription',
    'CurrentSubscription',
  );
  const pendingSubscriptionData = readField(
    responseData,
    'pendingSubscription',
    'PendingSubscription',
  );
  const guidanceData = readField(responseData, 'guidance', 'Guidance');
  const pcHeroBadgeData = readField(responseData, 'pcHeroBadge', 'PcHeroBadge');
  const promotion = isRecord(promotionData) ? promotionData : {};
  const paymentSummary = isRecord(paymentSummaryData) ? paymentSummaryData : {};
  const currentSubscription = isRecord(currentSubscriptionData)
    ? currentSubscriptionData
    : undefined;
  const pendingSubscription = isRecord(pendingSubscriptionData) ? pendingSubscriptionData : {};
  const guidance = isRecord(guidanceData) ? guidanceData : {};
  const pcHeroBadge = isRecord(pcHeroBadgeData) ? pcHeroBadgeData : {};
  const guidanceType = readString(readField(guidance, 'type', 'Type'), '');
  const nestedPaymentResultId = getPositiveInteger(
    readField(pendingSubscription, 'paymentResultId', 'PaymentResultId'),
    fallbackPolicy.pendingSubscription.paymentResultId,
  );
  const legacyPendingPaymentResultId = readString(
    readField(responseData, 'pendingPaymentResultId', 'PendingPaymentResultId'),
    fallbackPolicy.pendingPaymentResultId ?? '',
  );

  return {
    productName: readString(
      readField(responseData, 'productName', 'ProductName'),
      fallbackPolicy.productName,
    ),
    subscriptionNo: readNumber(
      readField(responseData, 'subscriptionNo', 'SubscriptionNo'),
      fallbackPolicy.subscriptionNo,
    ),
    billingCycle: readString(
      readField(responseData, 'billingCycle', 'BillingCycle'),
      fallbackPolicy.billingCycle,
    ),
    membershipState: resolveMembershipState(
      readField(responseData, 'membershipState', 'MembershipState'),
      fallbackPolicy.membershipState,
    ),
    hasBillingMethod: readBoolean(
      readField(responseData, 'hasBillingMethod', 'HasBillingMethod'),
      fallbackPolicy.hasBillingMethod,
    ),
    pendingPaymentResultId:
      legacyPendingPaymentResultId ||
      (nestedPaymentResultId > 0 ? String(nestedPaymentResultId) : undefined),
    currentSubscription: currentSubscription
      ? {
          subscriptionNo: readOptionalNumber(
            readField(currentSubscription, 'subscriptionNo', 'SubscriptionNo'),
          ),
          channel: readString(
            readField(currentSubscription, 'channel', 'Channel'),
            fallbackPolicy.currentSubscription?.channel ?? '',
          ),
          expireDt: readString(
            readField(currentSubscription, 'expireDt', 'ExpireDt'),
            fallbackPolicy.currentSubscription?.expireDt ?? '',
          ),
          isExpiringSoon: readBoolean(
            readField(currentSubscription, 'isExpiringSoon', 'IsExpiringSoon'),
            fallbackPolicy.currentSubscription?.isExpiringSoon ?? false,
          ),
        }
      : fallbackPolicy.currentSubscription,
    pendingSubscription: {
      exists: readBoolean(
        readField(pendingSubscription, 'exists', 'Exists'),
        fallbackPolicy.pendingSubscription.exists,
      ),
      eligibleDt: readString(
        readField(pendingSubscription, 'eligibleDt', 'EligibleDt'),
        fallbackPolicy.pendingSubscription.eligibleDt,
      ),
      maxCount: readNumber(
        readField(pendingSubscription, 'maxCount', 'MaxCount'),
        fallbackPolicy.pendingSubscription.maxCount,
      ),
      paymentResultId: nestedPaymentResultId,
      status: readString(
        readField(pendingSubscription, 'status', 'Status'),
        fallbackPolicy.pendingSubscription.status,
      ),
    },
    guidance: {
      type: guidanceType || fallbackPolicy.guidance.type,
      headline: readString(
        readField(guidance, 'headline', 'Headline'),
        fallbackPolicy.guidance.headline,
      ),
      ctaLabel: readString(
        readField(guidance, 'ctaLabel', 'CtaLabel'),
        fallbackPolicy.guidance.ctaLabel,
      ),
    },
    isAnySubscribed: readBoolean(
      readField(responseData, 'isAnySubscribed', 'IsAnySubscribed'),
      fallbackPolicy.isAnySubscribed,
    ),
    isPrePayment: readBoolean(
      readField(responseData, 'isPrePayment', 'IsPrePayment'),
      fallbackPolicy.isPrePayment,
    ),
    regularAmount: readNumber(
      readField(responseData, 'regularAmount', 'RegularAmount'),
      fallbackPolicy.regularAmount,
    ),
    paymentAmount: readNumber(
      readField(responseData, 'paymentAmount', 'PaymentAmount'),
      fallbackPolicy.paymentAmount,
    ),
    nextPaymentAmount: readNumber(
      readField(responseData, 'nextPaymentAmount', 'NextPaymentAmount'),
      fallbackPolicy.nextPaymentAmount,
    ),
    currency: readString(readField(responseData, 'currency', 'Currency'), fallbackPolicy.currency),
    trialDays: readNumber(
      readField(responseData, 'trialDays', 'TrialDays'),
      fallbackPolicy.trialDays,
    ),
    promotion: {
      enabled: readBoolean(
        readField(promotion, 'enabled', 'Enabled'),
        fallbackPolicy.promotion.enabled,
      ),
      badgeText: readString(
        readField(promotion, 'badgeText', 'BadgeText'),
        fallbackPolicy.promotion.badgeText,
      ),
      heroTitle: readString(
        readField(promotion, 'heroTitle', 'HeroTitle'),
        fallbackPolicy.promotion.heroTitle,
      ),
      summaryText: readString(
        readField(promotion, 'summaryText', 'SummaryText'),
        fallbackPolicy.promotion.summaryText,
      ),
      priceDescription: readString(
        readField(promotion, 'priceDescription', 'PriceDescription'),
        fallbackPolicy.promotion.priceDescription,
      ),
      periodText: readString(
        readField(promotion, 'periodText', 'PeriodText'),
        fallbackPolicy.promotion.periodText,
      ),
      discountMonths: readNumber(
        readField(promotion, 'discountMonths', 'DiscountMonths'),
        fallbackPolicy.promotion.discountMonths,
      ),
      discountAmount: readNumber(
        readField(promotion, 'discountAmount', 'DiscountAmount'),
        fallbackPolicy.promotion.discountAmount,
      ),
    },
    paymentSummary: {
      currentPaymentLabel: readString(
        readField(paymentSummary, 'currentPaymentLabel', 'CurrentPaymentLabel'),
        fallbackPolicy.paymentSummary.currentPaymentLabel,
      ),
      regularAmountLabel: readString(
        readField(paymentSummary, 'regularAmountLabel', 'RegularAmountLabel'),
        fallbackPolicy.paymentSummary.regularAmountLabel,
      ),
      billingCycleLabel: readString(
        readField(paymentSummary, 'billingCycleLabel', 'BillingCycleLabel'),
        fallbackPolicy.paymentSummary.billingCycleLabel,
      ),
      noticeText: readString(
        readField(paymentSummary, 'noticeText', 'NoticeText'),
        fallbackPolicy.paymentSummary.noticeText,
      ),
      ctaLabel: readString(
        readField(paymentSummary, 'ctaLabel', 'CtaLabel'),
        fallbackPolicy.paymentSummary.ctaLabel,
      ),
    },
    pcHeroBadge: {
      enabled: readBoolean(
        readField(pcHeroBadge, 'enabled', 'Enabled'),
        fallbackPolicy.pcHeroBadge.enabled,
      ),
      label: readString(readField(pcHeroBadge, 'label', 'Label'), fallbackPolicy.pcHeroBadge.label),
      type: resolvePcHeroBadgeType(
        readField(pcHeroBadge, 'type', 'Type'),
        fallbackPolicy.pcHeroBadge.type,
      ),
    },
  };
};

export const getPendingSubscriptionPaymentResultId = (policy: MobileMembershipPolicy) => {
  if (policy.pendingSubscription.paymentResultId > 0) {
    return policy.pendingSubscription.paymentResultId;
  }

  const legacyPaymentResultId = Number(policy.pendingPaymentResultId);

  return Number.isInteger(legacyPaymentResultId) && legacyPaymentResultId > 0
    ? legacyPaymentResultId
    : undefined;
};

export const getIsInAppToWebAvailable = (policy: MobileMembershipPolicy) => {
  return policy.guidance.type === MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_AVAILABLE;
};

const getKoreanCalendarYearMonth = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: KOREA_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
  }).formatToParts(date);
  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value);

  return { year, month };
};

export const getIsMayToJune2026FloatingBarOverridePeriod = (date = new Date()) => {
  const { year, month } = getKoreanCalendarYearMonth(date);

  return year === 2026 && month >= 5 && month <= 6;
};

export const getShouldOverrideMayToJune2026FloatingBarCopy = (date = new Date()) => {
  return getIsMayToJune2026FloatingBarOverridePeriod(date);
};

export const getMobileMembershipFloatingBarCopy = (
  policy: MobileMembershipPolicy,
  date = new Date(),
) => {
  const shouldOverrideFloatingBarCopy = getShouldOverrideMayToJune2026FloatingBarCopy(date);
  const defaultFloatingBarTitle = policy.guidance.headline || policy.promotion.heroTitle;
  const floatingBarTitle = shouldOverrideFloatingBarCopy
    ? MOBILE_MEMBERSHIP_JUNE_2026_APP_CONVERSION_FLOATING_TITLE
    : defaultFloatingBarTitle;
  const floatingBarButtonLabel = shouldOverrideFloatingBarCopy
    ? MOBILE_MEMBERSHIP_JUNE_2026_APP_CONVERSION_CTA_LABEL
    : policy.guidance.ctaLabel || '구독하기';

  return {
    floatingBarTitle,
    floatingBarButtonLabel,
    shouldShowFloatingBar: policy.promotion.enabled || Boolean(policy.guidance.headline),
  };
};

export const getIsInAppToWebPending = (policy: MobileMembershipPolicy) => {
  const pendingStatus = policy.pendingSubscription.status.toUpperCase();

  return (
    policy.guidance.type === MOBILE_MEMBERSHIP_GUIDANCE_INAPP_TO_WEB_PENDING ||
    policy.membershipState === MOBILE_MEMBERSHIP_WEB_CONVERSION_PENDING_STATE ||
    (policy.pendingSubscription.exists && pendingStatus === 'PENDING')
  );
};

export const getMobileMembershipSubscribeRoute = (policy: MobileMembershipPolicy) => {
  if (getIsInAppToWebPending(policy)) {
    return ROUTES.mobileMembershipActive;
  }

  if (policy.membershipState === MOBILE_MEMBERSHIP_WEB_SUBSCRIBED_STATE) {
    return ROUTES.mobileMembershipSubscribed;
  }

  return ROUTES.mobileMembershipPayment;
};

export const getMobilePendingEligibleDateLabel = (
  policy: MobileMembershipPolicy,
  fallbackLabel = '앱에서 확인 가능',
) => {
  const pendingDate = formatDateTimeLabel(policy.pendingSubscription.eligibleDt);
  if (pendingDate) return pendingDate;

  const expireDate = parseDateTimeLabel(policy.currentSubscription?.expireDt);
  if (!expireDate) return fallbackLabel;

  expireDate.setSeconds(expireDate.getSeconds() + 1);

  return formatDateLabel(expireDate);
};

export const createMobileCardPaymentPayload = ({
  cplatToken,
  isAnySubscribed,
  cardNo,
  expirationDate,
  password,
  cardOwnerInfo,
  policy,
}: MobileCardPaymentPayloadInput) => {
  const { subscriptionNo, isPrePayment } = policy ?? getSellerboxPlusPaymentPolicy(isAnySubscribed);

  return {
    cplatToken,
    subscriptionNo,
    isPrePayment,
    cardNo,
    expirationDate,
    password,
    cardOwnerInfo,
  };
};

export const createMobilePendingSubscriptionPayload = ({
  cplatToken,
}: MobilePendingSubscriptionPayloadInput) => {
  return {
    cplatToken,
    channel: MOBILE_MEMBERSHIP_CHANNEL,
  };
};

export const createMobileCardBillingPayload = ({
  cplatToken,
  cardNo,
  expirationDate,
  password,
  cardOwnerInfo,
}: MobileCardBillingPayloadInput) => {
  return {
    cplatToken,
    paymentMethod: '일반카드',
    cardNo,
    expirationDate,
    password,
    cardOwnerInfo,
    channel: MOBILE_MEMBERSHIP_CHANNEL,
  };
};

export const createMobileKakaoBillingReadyPayload = (
  cplatToken: string,
  policy: MobilePaymentPolicy,
) => {
  return {
    cplatToken,
    subscriptionNo: policy.subscriptionNo,
    isPrePayment: true,
    channel: MOBILE_MEMBERSHIP_CHANNEL,
  };
};

export const createMobileKakaoBillingPayload = ({
  cplatToken,
  pgToken,
  kakaoPayRequestInfo,
  kakaoPayResponseInfo,
}: MobileKakaoBillingPayloadInput) => {
  return {
    cplatToken,
    paymentMethod: '카카오페이',
    pgToken,
    kakaoPayRequestInfo,
    kakaoPayResponseInfo,
    channel: MOBILE_MEMBERSHIP_CHANNEL,
  };
};

export const createMobileKakaoReadyPayload = (
  cplatToken: string,
  isAnySubscribed: boolean,
  policy?: MobilePaymentPolicy,
) => {
  const { subscriptionNo, isPrePayment } = policy ?? getSellerboxPlusPaymentPolicy(isAnySubscribed);

  return {
    cplatToken,
    subscriptionNo,
    isPrePayment,
    channel: MOBILE_MEMBERSHIP_CHANNEL,
  };
};

const parseDateTimeLabel = (dateTimeLabel?: string) => {
  if (!dateTimeLabel) return null;

  const match = dateTimeLabel
    .trim()
    .match(/^(\d{4})[-.](\d{2})[-.](\d{2})(?:[T\s](\d{2}):(\d{2}):(\d{2}))?/);
  if (!match) return null;

  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTimeLabel = (dateTimeLabel: string) => {
  const date = parseDateTimeLabel(dateTimeLabel);

  return date ? formatDateLabel(date) : '';
};

const formatDateLabel = (date: Date) => {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('.');
};
